/**
 * Check if a user has access to a specific operation tile within a module.
 *
 * Rules:
 * - Admin users always have full access
 * - If allowed_operations is not set or empty for the module, all operations are visible (backward compat)
 * - If allowed_operations has entries for the module, only those operations are visible
 * - A tile may also declare `requires_any` (array of operation ids). If any of
 *   those operations is granted, the tile is visible. Used for parent/entry
 *   tiles that should unlock when ANY child operation is granted.
 *
 * @param {string} moduleId - The module identifier (e.g., 'sfg_wms', 'fg_wms')
 * @param {string} operationId - The operation tile id (e.g., 'create-pick-slip')
 * @param {string[]} [requiresAny] - Optional list of alternative op ids; tile
 *     is visible if ANY are granted (in addition to operationId itself).
 * @returns {boolean}
 */
export function hasOperationAccess(moduleId, operationId, requiresAny) {
  try {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');

    // Admin always has full access
    if (userData.role === 'Admin') return true;

    const allowedOps = userData.allowed_operations;

    // No allowed_operations at all = full access (backward compat for existing users)
    if (!allowedOps || typeof allowedOps !== 'object') return true;

    // Module not in allowed_operations = all operations visible
    const moduleOps = allowedOps[moduleId];
    if (!moduleOps || !Array.isArray(moduleOps) || moduleOps.length === 0) return true;

    // Direct match on this tile's id
    if (moduleOps.includes(operationId)) return true;

    // Parent-tile fallback: visible if ANY listed `requires_any` op is granted
    if (Array.isArray(requiresAny) && requiresAny.some(op => moduleOps.includes(op))) {
      return true;
    }

    return false;
  } catch {
    return true; // fail open
  }
}

/**
 * Filter an array of operation/tile objects based on user access.
 * Each tile may declare `requires_any: ['op-id-1', 'op-id-2']` to act as a
 * parent tile visible when ANY listed child op is granted.
 *
 * @param {string} moduleId - The module identifier
 * @param {Array} operations - Array of objects with 'id' property
 * @returns {Array} Filtered operations
 */
export function filterOperations(moduleId, operations) {
  return operations.filter(op => op.alwaysVisible || hasOperationAccess(moduleId, op.id, op.requires_any));
}

/**
 * Hourly Report tile on module dashboards: Admin, users granted Accounts →
 * Hourly Staff Report (or Payslip), and any Supervisor / Incharge role.
 */
export function canViewHourlyReport() {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    const role = (u.role || '').toLowerCase().replace(/[\s-]/g, '');
    if (['admin', 'siteadmin', 'super_admin'].includes(role)) return true;
    if (role.includes('supervisor') || role.includes('incharge')) return true;
    const acct = (u.allowed_operations || {}).accounts || [];
    return acct.includes('hourly-staff-report') || acct.includes('payslip');
  } catch {
    return false;
  }
}
